/**
 * ============================================================================
 * ILEM - Free Konjac Sponge gift engine
 * ============================================================================
 *
 * Replaces the old add-only `Buy1Get1Offer.free2Sponges()` in global.js
 * (now disabled) and the old add-only CartFreeGifts picker.
 *
 * Design
 * ------
 * This is a RECONCILER, not an adder. On every cart change it computes the
 * gift lines the cart *should* have and diffs that against the gift lines it
 * actually has, issuing both adds and removals. That is what makes the gift
 * disappear when the parent product is removed.
 *
 * Every gift line carries line-item properties that tie it to its parent:
 *
 *   _free_gift       'true'                  marks the line as a gift
 *   _gift_bucket     'face' | 'body' | 'charcoal'
 *   _gift_parent_id  '<parent product id>'   the association
 *   _Free            '_Konjac_Sponge'        existing theme flag - drives the
 *                                            "Your Free Konjac Sponge" label
 *                                            and hides qty/remove controls in
 *                                            sections/mini-cart.liquid
 *   'Free with'      '<parent title>'        customer-visible, shows in cart
 *                                            and on the order
 *
 * Properties beginning with `_` are hidden from the customer and the order;
 * "Free with" deliberately is not, so warehouse and support can see why the
 * sponge is there.
 *
 * Rules enforced
 * --------------
 *  1. Entitlement is 1 gift set per qualifying UNIT, per parent product.
 *  2. Only lines tagged `_free_gift` count as gifts. A sponge a customer paid
 *     for never cancels out their entitlement (the old code counted any
 *     sponge, so buyers silently lost their gift).
 *  3. Remove/reduce the parent -> the matching gift lines are removed/reduced.
 *  4. The gift product may never be in the cart on its own. Any sponge line
 *     without a `_free_gift` tag is deleted. This is the backstop behind the
 *     UI-level add-to-cart blocks.
 *  5. The customer may swap which sponge they get; the choice is remembered
 *     per bucket for the session and applied to every gift line in that
 *     bucket.
 *
 * Config comes from snippets/free-gift-config.liquid.
 */

/**
 * Anything that starts a checkout. The theme's native checkout button is
 * commented out in favour of GoKwik, but the others are still reachable on the
 * /cart page, so all of them are guarded.
 */
const CHECKOUT_SELECTORS = [
  '.gokwik-checkout button',
  '#gokwik-buy-now',
  '[name="checkout"]',
  '.shopify-payment-button__button',
  'a[href$="/checkout"]'
];

/** Never hold the customer longer than this, whatever goes wrong. */
const SYNC_MAX_MS = 8000;

class FreeGiftEngine {
  constructor(config) {
    this.cfg = config;
    this.busy = false;
    this.pending = false;
    this.debounceTimer = null;
    this.prefKey = 'ilem_free_gift_pref';
    this.pref = this.loadPref();

    // Checkout gating: true from the moment we know the cart changed until the
    // gift lines are settled.
    this.syncing = false;
    this.syncTimeout = null;
    this.queuedCheckout = null;

    this.bind();
    this.schedule();
  }

  /* ----------------------------------------------------------------- utils */

  get miniCart() {
    return document.querySelector('mini-cart');
  }

  loadPref() {
    try {
      return JSON.parse(window.sessionStorage.getItem(this.prefKey)) || {};
    } catch (e) {
      return {};
    }
  }

  savePref() {
    try {
      window.sessionStorage.setItem(this.prefKey, JSON.stringify(this.pref));
    } catch (e) {
      /* private mode - the preference just won't survive a page load */
    }
  }

  variantById(id) {
    return this.cfg.variants.find((v) => v.id === Number(id));
  }

  /** Face and charcoal buckets both draw from the face variants. */
  typeForBucket(bucket) {
    return bucket === 'body' ? 'body' : 'face';
  }

  /** Per-bucket rules from the config, tolerating the older `defaults` shape. */
  bucketRules(bucket) {
    const rules = (this.cfg.buckets && this.cfg.buckets[bucket]) || {};
    const fallbackDefault = this.cfg.defaults ? this.cfg.defaults[bucket] : undefined;
    const def = Number(rules.default !== undefined ? rules.default : fallbackDefault);

    // `allow` is the discount's "customer gets" list for this bucket, in
    // preference order. When present, NOTHING outside it may be granted -
    // not a swap preference, not a variant already in the cart, not a
    // sold-out fallback. strict:true is shorthand for allow:[default].
    let allow = Array.isArray(rules.allow) ? rules.allow.map(Number) : null;
    if (!allow && rules.strict) allow = [def];

    return { default: def, strict: !!rules.strict, allow };
  }

  /**
   * Which variant a bucket should use.
   *
   * Preference order: customer's saved swap, whatever is already in the cart,
   * the configured default, then any in-stock substitute. Every step is
   * filtered through the same eligibility test, so a bucket can never be
   * granted a variant its discount does not cover.
   *
   * Returns null for "cannot fulfil this bucket right now" - the caller grants
   * nothing rather than posting a line that would be charged or would 422.
   */
  variantForBucket(bucket, existingVariantId) {
    const wanted = this.typeForBucket(bucket);
    const rules = this.bucketRules(bucket);

    // Variants reserved for another bucket's offer. If offer A could consume
    // the sponge that the universal discount is the only one able to zero out,
    // the two discounts would compete and one sponge would be charged.
    // A variant is only reserved *against* buckets it is not the default for.
    const reserved = (this.cfg.reservedVariants || []).map(Number);

    const usable = (id) => {
      if (!id) return null;
      const variant = this.variantById(id);
      if (!variant) return null;
      if (variant.type !== wanted) return null;
      // available === false means sold out. /cart/add.js is bulk-atomic, so one
      // dead variant 422s the entire gift set on every cart change.
      if (variant.available === false) return null;
      if (reserved.indexOf(variant.id) !== -1 && variant.id !== rules.default) return null;
      // Restricted bucket: only what the discount covers.
      if (rules.allow && rules.allow.indexOf(variant.id) === -1) return null;
      return variant.id;
    };

    const candidates = [this.pref[bucket], existingVariantId, rules.default];
    for (const id of candidates) {
      const ok = usable(id);
      if (ok) return ok;
    }

    // Restricted bucket: substitute only within the discount-covered list.
    if (rules.allow) {
      for (const id of rules.allow) {
        const ok = usable(id);
        if (ok) return ok;
      }
      return null;
    }

    // Open bucket: any in-stock variant of the right type.
    const fallback = this.cfg.variants.find((v) => usable(v.id));
    return fallback ? fallback.id : null;
  }

  isGiftLine(item) {
    return !!(item.properties && item.properties._free_gift === 'true');
  }

  /* --------------------------------------------------------------- binding */

  bind() {
    // cart.js re-renders the drawer before dispatching these, so the fresh
    // (disabled) GoKwik button is already in the DOM by now - safe to revive.
    document.addEventListener('cart:updated', (event) => {
      this.reenableCheckoutButtons();
      if (event.detail && event.detail.source === 'free-gift-engine') return;
      this.schedule();
    });
    document.addEventListener('cart:update', () => {
      this.reenableCheckoutButtons();
      this.schedule();
    });
    // No debounce on an add - this is the window where the customer is most
    // likely to reach for checkout before the gift lands.
    document.addEventListener('ajaxProduct:added', () => this.schedule(0));
    document.addEventListener('ajaxProduct:removed', () => this.schedule(0));

    // Checkout guard. Capture phase on document, so it runs before the inline
    // `onclick="onCheckoutClick(this)"` on GoKwik's button and can stop the
    // event reaching it. Setting `disabled` would not work: GoKwik's own
    // MutationObserver re-enables `.gokwik-checkout button` every 700ms
    // (snippets/gokwik.liquid:83-123).
    document.addEventListener('click', (event) => this.guardCheckout(event), true);

    // Drawer opening is the last reliable signal on paths where a cart
    // mutation happened outside our listeners.
    const drawer = document.querySelector('details.cart-drawer-container');
    if (drawer) {
      new MutationObserver((mutations) => {
        this.schedule();

        // getDrawerState() hides the AI assistant and the BON loyalty button
        // while the drawer is open. It used to be called from the observers in
        // ComboOfferCartSync and Buy1Get1Offer (global.js), both of which are
        // now disabled - this is the only caller left. Without it the loyalty
        // button floats over the open cart drawer.
        if (typeof getDrawerState !== 'function') return;
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
            getDrawerState(mutation.target);
          }
        });
      }).observe(drawer, {
        attributes: true,
        attributeFilter: ['class']
      });
    }

    // Gift swap - delegated so it survives every drawer re-render.
    document.addEventListener('click', (event) => {
      const option = event.target.closest('[data-free-gift-option]');
      if (option) {
        event.preventDefault();
        event.stopPropagation();
        this.chooseVariant(option);
        return;
      }

      const toggle = event.target.closest('[data-free-gifts-toggle]');
      if (toggle) {
        event.preventDefault();
        event.stopPropagation();
        const wrapper = toggle.closest('[data-cart-free-gifts]');
        if (!wrapper) return;
        const collapsed = wrapper.classList.toggle('is-collapsed');
        toggle.setAttribute('aria-expanded', String(!collapsed));
      }
    });

    document.addEventListener('keydown', (event) => {
      const option = event.target.closest('[data-free-gift-option]');
      if (!option) return;
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      this.chooseVariant(option);
    });
  }

  chooseVariant(option) {
    const bucket = option.dataset.giftBucket;
    // A locked bucket cannot be swapped - its discount covers one variant.
    if (bucket && this.bucketRules(bucket).strict) return;
    const variantId = Number(option.dataset.variantId);
    if (!bucket || !variantId) return;

    const variant = this.variantById(variantId);
    if (!variant || variant.type !== this.typeForBucket(bucket)) return;
    if (option.classList.contains('is-selected')) return;

    this.pref[bucket] = variantId;
    this.savePref();

    // Optimistic UI so the tap feels instant; the re-render confirms it.
    const group = option.closest('[data-free-gift-group]');
    if (group) {
      group.querySelectorAll('[data-free-gift-option]').forEach((el) => {
        const selected = el === option;
        el.classList.toggle('is-selected', selected);
        el.setAttribute('aria-checked', String(selected));
      });
      group.classList.add('is-swapping');
    }

    this.schedule(0);
  }

  /* ------------------------------------------------------- checkout gating */

  /**
   * While the cart is in flux the customer must not be able to start checkout
   * with a half-applied gift. Rather than swallowing the click, remember it and
   * fire it again as soon as the cart settles - from the customer's side the
   * button just takes a moment.
   */
  guardCheckout(event) {
    const selector = CHECKOUT_SELECTORS.find((s) => event.target.closest(s));
    if (!selector) return;

    if (!this.syncing) {
      // A real click got through. Drop any queued replay so checkout can't be
      // triggered twice.
      this.cancelResume();
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    // Store the selector, not the node: a section re-render between now and
    // the resume would leave us holding a detached element.
    this.queuedCheckout = selector;
  }

  setSyncing(value) {
    if (this.syncing === value) return;
    this.syncing = value;

    // The click guard is live from t=0, but the dimmed styling waits a moment.
    // Most reconciles are a single /cart.js round trip with nothing to do, and
    // flashing the checkout button grey for 80ms on every cart change reads as
    // jank. Only show the state if the customer would actually notice a wait.
    //
    // Class goes on <html>, not <body>: GoKwik observes body with
    // {childList, subtree} and our own observer watches the drawer's class
    // attribute - neither should be woken by this.
    clearTimeout(this.dimTimeout);
    if (value) {
      this.dimTimeout = setTimeout(() => {
        document.documentElement.classList.add('free-gift-syncing');
      }, 250);
    } else {
      document.documentElement.classList.remove('free-gift-syncing');
    }

    clearTimeout(this.syncTimeout);
    if (value) {
      // Failsafe. If a request hangs, release rather than trap the customer.
      this.syncTimeout = setTimeout(() => {
        console.warn('[FreeGiftEngine] sync timed out, releasing checkout');
        this.setSyncing(false);
        this.resumeCheckout();
      }, SYNC_MAX_MS);
    }
  }

  cancelResume() {
    clearTimeout(this.resumeTimer);
    this.resumeTimer = null;
    this.queuedCheckout = null;
  }

  /**
   * Fire the click the customer made while the cart was syncing.
   *
   * A re-render puts a freshly rendered GoKwik button in the DOM, and
   * snippets/gokwik-checkout.liquid ships it `disabled` - GoKwik's own observer
   * only re-enables it after a 700ms debounce. Clicking a disabled button does
   * nothing, so poll briefly until it is live.
   */
  resumeCheckout() {
    const selector = this.queuedCheckout;
    if (!selector) return;
    this.queuedCheckout = null;

    let attempts = 0;
    const attempt = () => {
      this.reenableCheckoutButtons();
      const button = document.querySelector(selector);

      if (button && !button.disabled) {
        this.resumeTimer = null;
        button.click();
        return;
      }

      // ~3s at 100ms. If GoKwik never enables, stop quietly: the drawer is open
      // with a correct cart, and the customer can click again.
      if (++attempts > 30) {
        this.resumeTimer = null;
        return;
      }

      this.resumeTimer = setTimeout(attempt, 100);
    };

    attempt();
  }

  /* ---------------------------------------------------------- reconciling */

  schedule(delay = 120) {
    // If the programme is off, reconcile() returns before its try/finally and
    // would never release the gate.
    if (!this.cfg || !this.cfg.enabled || !this.cfg.giftProductId) return;

    // Gate checkout from the moment a cart change is known, not from when the
    // request starts - the gap between the two is the race being closed here.
    this.setSyncing(true);
    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => this.reconcile(), delay);
  }

  async reconcile() {
    if (!this.cfg || !this.cfg.enabled || !this.cfg.giftProductId) return;

    // Re-entrancy guard. Our own writes fire cart:updated, which would
    // otherwise re-enter mid-flight against a stale /cart.js snapshot - the
    // double-gift bug in the previous implementation.
    if (this.busy) {
      this.pending = true;
      return;
    }
    this.busy = true;

    try {
      const cart = await this.getCart();
      const plan = this.buildPlan(cart);

      if (Object.keys(plan.updates).length === 0 && plan.adds.length === 0) return;

      let state = null;
      if (Object.keys(plan.updates).length) state = await this.postUpdates(plan.updates);
      if (plan.adds.length) state = await this.postAdds(plan.adds);

      if (state) {
        this.renderSections(state.sections);
        document.dispatchEvent(
          new CustomEvent('cart:updated', { detail: { source: 'free-gift-engine' } })
        );
      }
    } catch (error) {
      console.error('[FreeGiftEngine]', error);
    } finally {
      this.busy = false;
      if (this.pending) {
        this.pending = false;
        this.schedule(0);
      } else {
        // Cart is settled - release the checkout button and fire any click the
        // customer made while they were waiting.
        this.setSyncing(false);
        this.resumeCheckout();
      }
    }
  }

  /**
   * Works out the exact set of cart writes needed. Pure - no network.
   * Exposed on the instance so it can be unit-tested from the console:
   *   freeGiftEngine.buildPlan(await freeGiftEngine.getCart())
   */
  buildPlan(cart) {
    const giftProductId = Number(this.cfg.giftProductId);
    const giftWrapProductId = Number(this.cfg.giftWrapProductId || 0);
    const wrapProperty = this.cfg.giftWrapProperty || 'Gift Wrap For';
    const offerA = (this.cfg.offerA || []).map(Number);
    const offerB = (this.cfg.offerB || []).map(Number);
    // Universal tier: everything that is not offer A and not excluded earns one
    // sponge per unit. Falls back to the old offerB allow-list when off, so a
    // partial deploy grants fewer gifts rather than more.
    const universal = this.cfg.universalOffer === true;
    const excluded = (this.cfg.excludedProductIds || []).map(Number);
    // Products that are listed but never purchasable (Theme settings ->
    // Display-only products). Unlike gift wrapping there is no tagged
    // exception: nothing legitimately adds one, so every line is stray.
    const displayOnly = (this.cfg.displayOnlyProductIds || []).map(Number);

    const parents = new Map(); // parentProductId -> { offer, qty, title }
    const giftLines = [];
    const strayGiftProductLines = [];
    const wrapLines = [];

    // Everything a gift-wrap line could be attached to. Collected up front
    // because a wrap line can appear before its parent in cart.items.
    const presentVariantIds = new Set();
    const presentTitles = new Set();

    cart.items.forEach((item) => {
      const isWrapLine = giftWrapProductId > 0 && item.product_id === giftWrapProductId;
      const isDisplayOnly = displayOnly.includes(item.product_id);

      // Neither a wrap line nor a doomed display-only line may act as the
      // parent of a gift wrap - both are about to leave the cart.
      if (!isWrapLine && !isDisplayOnly) {
        presentVariantIds.add(Number(item.variant_id));
        if (item.product_title) presentTitles.add(item.product_title);
      }

      if (this.isGiftLine(item)) {
        giftLines.push(item);
        return;
      }

      // Rule 4: the gift product is never allowed in the cart untagged.
      if (item.product_id === giftProductId) {
        strayGiftProductLines.push(item);
        return;
      }

      // Display-only products: listed on the site, never sellable. The theme
      // hides their buy button and quick-add, but this is the backstop that
      // catches every other route - a hand-typed /cart/add, a stale cart, a
      // permalink. Deleted outright, and they never earn a gift.
      if (isDisplayOnly) {
        strayGiftProductLines.push(item);
        return;
      }

      // Gift wrapping may only arrive through the "Make this a gift" checkbox,
      // which tags the line with "Gift Wrap For" and "_Associated Product ID"
      // (assets/global.js, GiftWrap.addGiftWrap). An untagged line means it was
      // added some other way - drop it. Tagged ones are checked against their
      // parent after the loop. Never counts towards an offer either.
      if (isWrapLine) {
        const props = item.properties || {};
        const addedByCheckbox = props[wrapProperty] || props['_Associated Product ID'];
        if (addedByCheckbox) wrapLines.push(item);
        else strayGiftProductLines.push(item);
        return;
      }

      // Never earns a gift: gift cards (no physical order to attach it to),
      // and anything explicitly excluded in the config. The gift product and
      // gift wrapping have already returned above.
      if (item.gift_card === true) return;
      if (excluded.includes(item.product_id)) return;

      let offer = null;
      if (offerA.includes(item.product_id)) offer = 'A';
      else if (offerB.includes(item.product_id)) offer = 'B';
      else if (universal) offer = 'B';
      if (!offer) return;

      const existing = parents.get(item.product_id);
      parents.set(item.product_id, {
        offer,
        qty: (existing ? existing.qty : 0) + item.quantity,
        title: item.product_title || item.title
      });
    });

    // Desired state, keyed `<parentId>|<bucket>`.
    const desired = new Map();
    parents.forEach((parent, parentId) => {
      const buckets = parent.offer === 'A' ? ['face', 'body'] : ['charcoal'];
      buckets.forEach((bucket) => {
        desired.set(`${parentId}|${bucket}`, {
          bucket,
          parentId,
          parentTitle: parent.title,
          qty: parent.qty,
          variantId: null // resolved below, once we know what is already there
        });
      });
    });

    const updates = {};
    const adds = [];
    const settled = new Set();

    giftLines.forEach((line) => {
      const bucket = line.properties._gift_bucket;
      const parentId = Number(line.properties._gift_parent_id);
      const key = `${parentId}|${bucket}`;
      const want = desired.get(key);

      // Parent gone, or a gift line we no longer recognise -> drop it.
      if (!want) {
        updates[line.key] = 0;
        return;
      }

      // Duplicate line for the same slot (legacy double-adds) -> drop it.
      if (settled.has(key)) {
        updates[line.key] = 0;
        return;
      }

      const variantId = this.variantForBucket(bucket, line.variant_id);

      // Nothing in stock for this bucket - leave the existing gift line
      // alone rather than swapping it for something that cannot be added.
      if (!variantId) {
        settled.add(key);
        return;
      }

      want.variantId = variantId;
      settled.add(key);

      if (line.variant_id !== variantId) {
        // Swap: remove the old line, add the chosen variant.
        updates[line.key] = 0;
        adds.push(this.addPayload(want));
        return;
      }

      if (line.quantity !== want.qty) updates[line.key] = want.qty;
    });

    desired.forEach((want, key) => {
      if (settled.has(key)) return;
      want.variantId = this.variantForBucket(want.bucket, null);
      if (!want.variantId) return; // whole bucket sold out - grant nothing
      adds.push(this.addPayload(want));
    });

    strayGiftProductLines.forEach((line) => {
      updates[line.key] = 0;
    });

    // Gift wrapping follows its parent out of the cart. GiftWrap in global.js
    // only removes the wrap when the customer unticks the box - deleting the
    // wrapped product left the ₹499 wrap behind, the same way the sponge used
    // to be stranded.
    //
    // "_Associated Product ID" is the parent's VARIANT id (mini-cart.liquid
    // passes data-gift-bento-id="{{ item.variant.id }}"). Older lines may only
    // have the title, so that is the fallback.
    wrapLines.forEach((line) => {
      const props = line.properties || {};
      const parentVariantId = Number(props['_Associated Product ID']);
      const parentPresent = parentVariantId
        ? presentVariantIds.has(parentVariantId)
        : presentTitles.has(props[wrapProperty]);

      if (!parentPresent) updates[line.key] = 0;
    });

    return { updates, adds };
  }

  addPayload(want) {
    return {
      id: want.variantId,
      quantity: want.qty,
      properties: {
        _free_gift: 'true',
        _gift_bucket: want.bucket,
        _gift_parent_id: String(want.parentId),
        _Free: '_Konjac_Sponge',
        'Free with': want.parentTitle
      }
    };
  }

  /* ------------------------------------------------------------- transport */

  async getCart() {
    const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    return response.json();
  }

  sectionIds() {
    const miniCart = this.miniCart;
    if (miniCart && typeof miniCart.getSectionsToRender === 'function') {
      return miniCart.getSectionsToRender().map((section) => section.id);
    }
    return ['mini-cart', 'cart-icon-bubble', 'mobile-cart-icon-bubble'];
  }

  postUpdates(updates) {
    return this.post('/cart/update.js', { updates });
  }

  postAdds(items) {
    return this.post('/cart/add.js', { items });
  }

  async post(url, payload) {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        ...payload,
        sections: this.sectionIds(),
        sections_url: window.location.pathname
      })
    });

    const state = await response.json();

    if (state.status === 422 || state.status === 404) {
      // Most likely a gift variant went out of stock. Surface it the way the
      // rest of the theme does rather than failing silently.
      document.dispatchEvent(
        new CustomEvent('ajaxProduct:error', {
          detail: { errorMessage: state.description || state.message }
        })
      );
      throw new Error(state.description || state.message || 'Cart request failed');
    }

    return state;
  }

  /**
   * Re-render the cart sections WITHOUT opening the drawer.
   * MiniCart.renderContents() calls open(), which made the drawer pop open on
   * every background reconcile.
   */
  renderSections(sections) {
    if (!sections) return;

    const miniCart = this.miniCart;
    const targets =
      miniCart && typeof miniCart.getSectionsToRender === 'function'
        ? miniCart.getSectionsToRender()
        : [
            { id: 'mini-cart', selector: '.shopify-section' },
            { id: 'cart-icon-bubble', selector: '.shopify-section' },
            { id: 'mobile-cart-icon-bubble', selector: '.shopify-section' }
          ];

    targets.forEach((target) => {
      const element = document.getElementById(target.id);
      const html = sections[target.id];
      if (!element || !html) return;

      const parsed = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector(target.selector || '.shopify-section');

      if (parsed) element.innerHTML = parsed.innerHTML;
    });

    this.reenableCheckoutButtons();
  }

  /**
   * A re-rendered cart section brings back a fresh GoKwik button, and
   * snippets/gokwik-checkout.liquid ships it `disabled`. GoKwik re-enables it
   * from a MutationObserver on a 700ms debounce (snippets/gokwik.liquid:120),
   * and because our render is a second DOM mutation it restarts that timer -
   * so the customer watches a dead checkout button for up to ~1.4s after the
   * gifts have already landed.
   *
   * Do what GoKwik's own handler does, immediately. Gated on the SDK's global
   * actually existing: the button's inline onclick calls onCheckoutClick(), so
   * enabling it before the SDK has loaded would just throw on click.
   * GoKwik's debounce still runs afterwards and is idempotent.
   */
  reenableCheckoutButtons() {
    if (typeof window.onCheckoutClick !== 'function') return;

    document.querySelectorAll('.gokwik-checkout button').forEach((button) => {
      button.disabled = false;
      button.classList.remove('disabled');
    });
  }
}

/* -------------------------------------------------------------------------- */

function initFreeGiftEngine() {
  if (window.freeGiftEngine) return;

  const node = document.getElementById('ilem-free-gift-config');
  if (!node) return;

  let config;
  try {
    config = JSON.parse(node.textContent);
  } catch (error) {
    console.error('[FreeGiftEngine] bad config', error);
    return;
  }

  if (!config.enabled) return;

  window.freeGiftEngine = new FreeGiftEngine(config);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initFreeGiftEngine);
} else {
  initFreeGiftEngine();
}
